import React, { useState, useEffect, Suspense } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { changeAppLanguage, normalizeLanguage, SUPPORTED_LANGUAGES } from '../lib/language';
import { LayoutDashboard, BarChart3, LogOut, BookOpen, X, Crown, History, AlertTriangle, LogIn, UserPlus, HelpCircle, Activity, FileClock, ListChecks, Languages, Users, Stethoscope, Settings, Trash2, Gift, Sparkles, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useExamNavLock } from '../contexts/ExamContext';
import { useTranslation } from 'react-i18next';
import { usePendingSummary } from '../hooks/usePendingSummary';
import { useDowngradeNotice } from '../hooks/useDowngradeNotice';
import { IS_APP_BUILD } from '../lib/buildTarget';
// LAPIDA post-t7-2c (D1, #640): murio el import de `AppStoreBloque`: la barra ya no monta su tarjeta.
// TANDA 28b (firma #230): la etiqueta «-20%» del renglón Upgrade.
import { fetchReferralState, useVistaReferidos, datosDeVista } from '../lib/referidos';
// dashboard-rediseno (4.1 y 4.2): el ancho del menu y su lugar debajo de la barra salen de la fuente unica.
import { MENU_ANCHO_CLASS, MENU_DEBAJO_DE_LA_BARRA_CLASS, MENU_ABIERTO_EN_TABLET_CLASS, VELO_DEL_MENU_DE_TABLET_CLASS } from '../lib/medidasDelChasis';
// tutor-1 (C): el renglon Tutor solo con el tutor de la sesion encendido (o el ultimo `on` que el aparato recuerda).
import { useTutorDeLaSesion, tutorVisible } from '../lib/tutorDeLaSesion';
import { useMedia } from '../lib/useEsAncho';

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || '';

// ── R53 (sección D del censo) — EL DRAWER, ESPEJO EXACTO DE LA S-7 ──────────
//
// EL SÍNTOMA: en el drawer de More, un toque sostenido levantaba la selección
// azul, la lupa y el menú de copiar; y sobre las anclas, además, el callout
// nativo de iOS —la vista previa de la liga con la URL interna
// `capacitor://localhost/...` a la vista— y el arrastre.
//
// LA CAUSA, medida: no hay nada que lo apague. Los únicos escritores de
// `.app-noselect-text` y `.nav-no-callout` eran `MobileBottomNav.js` y
// `StudyCenter.js`; ni un solo nodo del árbol del Sidebar las llevaba.
//
// EL PATRÓN, copiado de la S-7 sin inventar nada:
//
//   · `.nav-no-callout` EN LOS ÍTEMS, jamás en el chasis, y concatenada al FINAL
//     de la cadena existente. Precedente literal: `MobileBottomNav.js:436`
//     (`const navItemClass = \`${tabBoxClass} nav-no-callout\`;`), con su razón:
//     los prefijos de clase se comparan carácter por carácter en las suites.
//   · `draggable={false}` en cada ancla. Precedente: `MobileBottomNav.js:482`.
//   · LOS DOS van en los DOS builds — es lo que la S-7 firmó: quitarle el callout
//     de liga a una barra de navegación es correcto también en la web móvil.
//   · CERO listeners. Nada de `contextmenu` ni de `dragstart`, y nada en
//     `document`: el CSS es el mecanismo. Si el smoke en el iPhone demuestra que
//     no basta, la intercepción iría en estos nodos y nunca en `document`.
//
// Y LA VARIANTE ANGOSTA para `.app-noselect-text`, que es la que respeta la regla
// que el propio S-7 dejó firmada en `index.css:335-337`: «No ponerla nunca en un
// contenedor: apagar la selección de un bloque entero se lleva por delante texto
// que el usuario sí quiere copiar». Por eso NO va en el `<aside>` entero, sino en
// el `<nav>` de la lista y en el bloque de sesión — los dos sitios donde todo el
// texto es de navegación y ninguno es copiable. Gateada por `IS_APP_BUILD` en el
// JSX (la regla en sí viaja en los dos bundles de CSS sin hacer nada).
//
// LO QUE JSDOM NO PUEDE JURAR (lo firma el smoke en el iPhone): que iOS respete
// el callout apagado dentro de un drawer con `translate-x` y `overflow-y: auto`.
// Los tests aseveran clases y cableado — misma limitación que la S-7 declaró.
const NAV_NO_CALLOUT = 'nav-no-callout';
/* #447 — EL VESTIDO EN REPOSO DE UN RENGLÓN DEL MENÚ, EN UN SOLO SITIO.
   Lo comparten el pintor de los renglones (`renderNavItem`) y la fila plegable
   de «Idioma», que es un botón y no una liga pero tiene que leerse igual que sus
   vecinas. Vive en una constante y no copiado dos veces por dos razones: para
   que no puedan divergir, y porque el censo del apretón táctil (firma #177,
   `lib/tanda11.apretonTactil.test.js`) se ancla en esta cadena y EXIGE que
   aparezca exactamente UNA vez en el archivo. Los cuatro tokens que ese censo
   pide van todos en esta misma línea, que es lo que la guarda mira. */
const NAV_ROW_IDLE = 'hover:text-[#f0f0f0] hover:bg-[#181818] active:text-[#f0f0f0] active:bg-[#181818] active:duration-0';
/* dashboard-rediseno (seccion "Menu lateral"): el color en reposo va aparte del apreton, porque el grupo de la
   cuenta lleva otro gris (#A8A8A8) que el de estudio (#C8C8C8); el renglon encendido es el mismo para todos. */
const NAV_TEXTO_ESTUDIO = 'text-[#C8C8C8]';
const NAV_TEXTO_CUENTA = 'text-[#A8A8A8]';
const NAV_ENCENDIDO = 'bg-[#1C130D] text-[#F7A06A] font-semibold';
/* El menu de iconos (de 768 a 1023): el renglon centra su icono, el nombre se esconde (queda como `aria-label` y
   `title`) y los titulos de grupo tambien; la linea divisoria se queda. */
// tutor-1 (D, firma 8): con el menu de tablet ABIERTO estas dos se apagan (el renglon pinta su nombre); por eso el
// componente las vuelve a declarar con el mismo nombre, segun el estado.
const NAV_RENGLON_ICONOS_CERRADO = 'md:max-lg:justify-center md:max-lg:px-0';
const SOLO_CON_NOMBRE_CERRADO = 'md:max-lg:hidden';
const CONSULTA_ICONOS = '(min-width: 768px) and (max-width: 1023.98px)';
const NOSELECT_APP = IS_APP_BUILD ? ' app-noselect-text' : '';

// Mobile drawer width (F4.3a-fix, Carlos's exact rule): the drawer HUGS its
// content — the gap from the left edge to where the content starts must equal
// the gap from where the content ends to the right edge. w-fit sizes the aside
// to its widest row (longest nav label, EN or ES) and the existing symmetric
// paddings (nav p-3 + item px-4) provide the equal breathing room on both
// sides; 85vw is only a safety cap for extreme cases. From 768 up the rail has a
// fixed width, and since dashboard-rediseno it comes from lib/medidasDelChasis.
const DRAWER_WIDTH_CLASS = 'w-fit max-w-[85vw]';

// COS-25 — tope del encabezado del drawer (logo + X). Base 1.25rem = el `p-5`
// original, más lo que mida la barra de estado/notch. Exportada para que la
// suite fije el calc() exacto y detecte una reversión a `p-5`/`pt-5` fijo.
export const SIDEBAR_HEADER_TOP_CLASS = 'pt-[calc(1.25rem+env(safe-area-inset-top,0px))]';

/** borrar-rotas (A.6 a): el nombre de la cabecera del menu del telefono, en la linea de los iconos de los renglones. */
export const ALINEACION_DEL_NOMBRE_EN_EL_MENU = 'ml-2';

const navItemsConfig = [
  { to: '/dashboard', icon: LayoutDashboard, key: 'dashboard' },
  { to: '/pending', icon: FileClock, key: 'pending' },
  { to: '/study', icon: BookOpen, key: 'study_center' },
  // tutor-1 (C): el Tutor, entre el Centro de Estudio y el Historial (sin renglon con la perilla TUTOR apagada).
  { to: '/tutor', icon: Sparkles, key: 'tutor' },
  { to: '/study/log', icon: History, key: 'study_log' },
  { to: '/analytics', icon: BarChart3, key: 'analytics' },
  { to: '/upgrade', icon: Crown, key: 'upgrade' },
  { to: '/earn-credits', icon: Gift, key: 'earn_credits' },
];

// Remate de post-t7-2c (R1, "1. si" de Carlos del 15-oct): LOS RENGLONES QUE SOLO VE QUIEN TIENE CUENTA. Su pantalla
// rebota al invitado a /signup, asi que el renglon no se le pinta: es la regla de #435 para `settings`, y la de "Gana
// creditos gratis" es la MISMA (una lista, un filtro, para los dos grupos del menu).
const SOLO_CON_CUENTA = ['settings', 'earn_credits'];

/* #447 — EL GRUPO DE ABAJO, Y ES UNO SOLO: Ajustes · Soporte · Idioma · Cerrar sesión.

   «Ajustes» y «Soporte» VIVEN AQUÍ desde esta firma. Antes cerraban la lista de
   arriba (#435 puso Ajustes justo debajo de Soporte) y el idioma y el cierre de
   sesión vivían abajo, en el bloque fijo: dos grupos separados por un borde, con
   la lista de arriba desplazándose y el de abajo clavado. Los cuatro renglones
   son de la MISMA clase — ajustes de la cuenta, no navegación de contenido —, así
   que van juntos y en el orden que firmó Carlos.

   EL VESTIDO NO CAMBIA: los dos renglones se pintan con `renderNavItem`, el mismo
   que pinta los de arriba, así que heredan el renglón, el icono de 16 px, el
   candado de examen, el `onClose` del drawer y su `data-testid`. Y la regla de
   invitados de #435 tampoco: `settings` se sigue escondiendo para quien no tiene
   cuenta, porque la pantalla lo rebota a /signup.

   `MobileBottomNav.js` no se toca. */
const bottomItemsConfig = [
  { to: '/settings', icon: Settings, key: 'settings' },
  { to: '/support', icon: HelpCircle, key: 'support' },
];

// post-t7-2b (D2): UN SOLO RENGLON ENCENDIDO. Un NavLink sin `end` se enciende en su ruta y en todas sus
// sub-rutas, asi que en /study/log se encendian "Centro de Estudio" (/study) y "Historial de Estudio". Regla: un
// renglon se enciende si su NavLink esta activo Y ningun otro renglon del menu casa de forma MAS precisa con la
// ruta. El Centro sigue encendido en /study y en las sesiones (/study/adaptive, /study/exam, /study/flashcards), que
// no son renglones. Estas son TODAS las rutas que el menu pinta (una prueba las compara con este archivo); hoy el
// unico par que comparte prefijo es /study y /study/log.
export const RUTAS_DEL_MENU = [
  ...navItemsConfig.map((i) => i.to), '/manage-documents', '/groups',
  ...bottomItemsConfig.map((i) => i.to), '/admin/dashboard', '/admin/errors', '/diag/ready-chain', '/login', '/signup',
];

const rutaCasa = (to, ruta) => ruta === to || ruta.startsWith(`${to}/`);

export function renglonEncendido(to, ruta, activo) {
  if (!activo) return false;
  if (!ruta) return true;
  return !RUTAS_DEL_MENU.some((otra) => otra !== to && otra.length > to.length && rutaCasa(otra, ruta));
}

// La ruta de la barra, del router. Sin router (un doble de react-router-dom sin `useLocation`, en pruebas viejas) da
// '' y el renglon se pinta como lo dice el NavLink. La llamada va siempre y en el mismo orden (el mismo molde que
// `useRutaActual` de routeGuards.js); la regla del build ve el `try` como condicion: por eso la excepcion.
function useRutaDelMenu() {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useLocation().pathname || '';
  } catch {
    return '';
  }
}

/* t7-1 (#466) -- "BORRAR MIS DATOS" DEL INVITADO VIVE EN ESTE MENU, DEBAJO DE
   "CREAR CUENTA". Antes era un enlace del banner de invitado del Panel; es la puerta
   del borrado de cuenta del invitado que pide la App Store (5.1.1(v)), y desde aqui
   se alcanza en todas las pantallas y en la app (su boton "Mas" abre este cajon).
   Misma llave, misma ventana (`DeleteAccountModal`) y misma ruta del servidor.

   LA VENTANA SE CARGA EN DIFERIDO, Y NO ES ESTILO: `DeleteAccountModal` importa
   `lib/api`, que arrastra `src/i18n.js`, y las suites que montan este menu sin doblar
   `lib/api` se caerian al arrancar si se importara arriba. `SettingsPage` ya la
   importa de forma estatica, asi que en el build esto resuelve del paquete principal
   sin pedir nada a la red. */
const DeleteAccountModal = React.lazy(() => import('./DeleteAccountModal'));

export default function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  // Mid-session lock — shared source of truth with MobileBottomNav (see ExamContext).
  const { isExamActive, blockNavigation: handleExamBlock } = useExamNavLock();
  const navigate = useNavigate();
  const rutaDelMenu = useRutaDelMenu();   // post-t7-2b (D2)
  const enIconos = useMedia(CONSULTA_ICONOS, false);   // dashboard-rediseno: el menu de 80
  // tutor-1 (D, firma 8): en la tablet (el menu de iconos) un toque en cualquier icono abre el menu completo ENCIMA
  // del contenido, sin empujarlo, con un velo; se cierra tocando el velo, con la flecha, al elegir un destino o con
  // Escape. Desde 1024 nada cambia.
  const [abiertoEnTablet, setAbiertoEnTablet] = useState(false);
  const abiertoEnIconos = enIconos && abiertoEnTablet;
  const NAV_RENGLON_ICONOS = abiertoEnIconos ? '' : NAV_RENGLON_ICONOS_CERRADO;
  const SOLO_CON_NOMBRE = abiertoEnIconos ? '' : SOLO_CON_NOMBRE_CERRADO;
  useEffect(() => { if (!enIconos) setAbiertoEnTablet(false); }, [enIconos]);
  useEffect(() => {
    if (!abiertoEnIconos) return undefined;
    const tecla = (e) => { if (e.key === 'Escape') setAbiertoEnTablet(false); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [abiertoEnIconos]);
  const cerrarTodo = () => { setAbiertoEnTablet(false); if (onClose) onClose(); };
  const tutorDeLaSesion = useTutorDeLaSesion();
  /* TANDA 12 (firma #182) — `settings` entra a la lista para que el renglón de
     Diagnóstico pueda leer sus llaves con el namespace por delante, SIN estrenar
     texto: son las mismas `diagnostics.*` que ya vivían en `settings.json`.
     `common` sigue de PRIMERO, así que es el namespace por defecto y todas las
     llaves sueltas de este archivo (`nav.*`, `sidebar.*`…) resuelven exactamente
     como antes. Cero peticiones nuevas: `src/i18n.js` ya declara los ocho
     namespaces al arrancar, así que `settings` se carga con los demás. */
  const { t, i18n } = useTranslation(['common', 'settings']);

  /* TANDA 28b (firma #230, §G.2) — el estado de referidos para la etiqueta.
     UNA peticion por CARGA DE PAGINA en total: `fetchReferralState` guarda la
     promesa en un memo de modulo, asi que este menu, el nav de abajo, el Panel y
     `/upgrade` comparten la misma respuesta en vez de pedir cuatro veces. El
     chasis se monta una sola vez por carga y no se re-monta al navegar, asi que
     esto tampoco vuelve a correr al cambiar de pantalla. En build de app la
     funcion sale por lo bajo sin tocar la red (#219). */
  const vistaReferidos = useVistaReferidos(user);
  const deVistaRef = datosDeVista(vistaReferidos);
  const [refDescuento, setRefDescuento] = useState(0);
  /* EN MODO VISTA NO SE PREGUNTA NADA. La etiqueta vive en el CHASIS, no en una
     pagina, asi que sin esto `?vista=amigo` pintaria las franjas y los tachados
     de la pagina pero dejaria el renglon Upgrade sin etiqueta — justo la mitad
     que el smoke comprueba. El chasis se monta una vez por CARGA de pagina, y
     Carlos llega tecleando la URL, asi que el parametro esta ahi al montar.
     La dependencia es un booleano y no el objeto: `datosDeVista` devuelve una
     copia nueva en cada render (ver `AvisoReferidos.js`). */
  const enVistaRef = Boolean(deVistaRef);
  // 28b-fix (H-1): la cuenta va en la peticion. El memo de modulo lleva pegado
  // el `userId` con el que se pidio, asi que cambiar de sesion sin recargar
  // —logout + login en la misma SPA— ya no sirve el descuento del anterior.
  const userId = user?.id;
  useEffect(() => {
    if (IS_APP_BUILD || !user || enVistaRef) return undefined;
    let vivo = true;
    fetchReferralState(userId).then((s) => {
      if (vivo && s.enabled) setRefDescuento(s.discount_pending || 0);
    });
    return () => { vivo = false; };
  }, [user, userId, enVistaRef]);
  const descuentoReferido = deVistaRef
    ? deVistaRef.state.discount_pending > 0
    : refDescuento > 0;
  const currentLang = normalizeLanguage(i18n.language);
  /* #447 — el plegado del idioma vive SOLO en memoria, y es a propósito: es el
     estado de un acordeón, no una preferencia. Nace cerrado en cada montaje del
     drawer; lo que SÍ persiste — el idioma elegido — lo sigue guardando
     `changeAppLanguage`, exactamente como antes de esta firma. */
  const [idiomaAbierto, setIdiomaAbierto] = useState(false);
  // #466 -- la ventana de borrar los datos del invitado, abierta desde su renglon.
  const [borrarDatosAbierto, setBorrarDatosAbierto] = useState(false);
  const { groupCount } = usePendingSummary();
  // Scheduled-downgrade gate (same signal as the banner): show the "manage documents" item
  // only during the grace window, and let it disappear on its own once resolved.
  const { notice: downgradeNotice } = useDowngradeNotice();
  /* TANDA 12 (firma #182) — EL MISMO CINTURÓN QUE YA TENÍAN AJUSTES Y EL VISOR.
     El `!!ADMIN_EMAIL` de delante tapa el caso degenerado: si un build no define
     la dirección, la constante queda en cadena vacía y la comparación cruda
     `algo === ''` es VERDADERA para una cuenta con el correo vacío — o sea que el
     menú fallaba ABIERTO. Con la cadena vacía descartada antes de comparar, sin
     dirección configurada NADIE es administrador y las TRES entradas de este
     bloque se quedan cerradas, que es el fallo correcto. Honestidad sobre el
     alcance: ese correo vacío hoy no existe (los cuatro altos del servidor
     escriben uno no vacío, invitados incluidos). Cuesta cuatro caracteres. */
  const isAdmin = !!ADMIN_EMAIL && user?.email?.toLowerCase() === ADMIN_EMAIL;
  const isGuest = user?.is_guest;
  // PILOTO-1 (firma #79) — la bandera institucional llega en `/auth/me` y es
  // SIEMPRE booleana; su ausencia (todas las cuentas de hoy) es `false`, así que
  // para el consumidor actual este menú queda carácter por carácter como estaba.
  const isInstitutional = Boolean(user?.is_institutional);
  // Pending section is authenticated-only (guests can't hold pending parts).
  // #435: «Ajustes» se esconde para invitados por la MISMA razón que «Pendientes»
  // — la pantalla los rebota a /signup (SettingsPage.js: `if (user?.is_guest)`),
  // así que un renglón que sólo sabe expulsar no es una entrada, es una trampa.
  const soloConCuenta = (item) => SOLO_CON_CUENTA.includes(item.key) && isGuest;
  // dashboard-rediseno (firma 1): Upgrade no se le ensena al Pro, con la regla de hoy de quien es Pro.
  const esPro = user?.plan === 'pro';
  const navItems = navItemsConfig
    .filter(item => !(item.key === 'pending' && isGuest) && !soloConCuenta(item) && !(item.key === 'upgrade' && esPro)
      && !(item.key === 'tutor' && !tutorVisible(tutorDeLaSesion, user)))
    .map(item => ({ ...item, label: t(`nav.${item.key}`) }));
  // #447 — el grupo de abajo. MISMA regla de invitados que en #435: `settings`
  // no se pinta para quien la pantalla rebota a /signup. «Soporte» sí, como hoy.
  const bottomItems = bottomItemsConfig
    .filter(item => !soloConCuenta(item))
    .map(item => ({ ...item, label: t(`nav.${item.key}`) }));
  // Insert the document-selection entry right under Dashboard, only during the grace window.
  if (downgradeNotice.show && !isGuest) {
    navItems.splice(1, 0, {
      to: '/manage-documents', icon: ListChecks, key: 'manage_documents',
      label: t('nav.manage_documents'),
    });
  }
  // PILOTO-1 — «Mis grupos», justo debajo del Centro de estudio. Se inserta
  // SOLO con bandera: sin ella el arreglo `navItems` es el mismo objeto de
  // siempre y el drawer no cambia ni un pixel. Va detrás del candado de examen
  // igual que el resto (es navegación, y a media sesión no se navega).
  if (isInstitutional && !isGuest) {
    const studyIdx = navItems.findIndex(i => i.key === 'study_center');
    navItems.splice(studyIdx >= 0 ? studyIdx + 1 : navItems.length, 0, {
      to: '/groups', icon: Users, key: 'groups', label: t('nav.groups'),
    });
  }

  const navBadge = (item) => {
    if (item.key === 'pending' && groupCount > 0) {
      return (
        <span
          className="ml-auto bg-[#FF6B00] text-white text-[12px] font-semibold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
          data-testid="sidebar-pending-badge"
        >
          {groupCount}
        </span>
      );
    }
    /* TANDA 28b (firma #230, diseño §7.4) — LA ETIQUETA «-20%» EN UPGRADE.
       Sale SÓLO con descuento pendiente; para todos los demás este renglón queda
       exactamente como hoy, píxel por píxel, que es lo que #257 firmó (una
       segunda etiqueta para quien NO tiene descuento se descartó por engañosa).
       Reusa la píldora que ya existe en este menú —la de arriba, del contador de
       grupos— porque es la misma clase de marca en el mismo sitio: dos píldoras
       distintas a dos renglones de distancia se leerían como dos cosas. */
    if (item.key === 'upgrade' && descuentoReferido) {
      return (
        <span
          className="ml-auto bg-[#FF6B00] text-white text-[12px] font-semibold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
          data-testid="sidebar-referral-badge"
        >
          {t('dashboard:referral.badge')}
        </span>
      );
    }
    return null;
  };

  // COS-22-R — EN APP, ESTE CALLER YA NO ELIGE DESTINO: LO ELIGE EL GUARD.
  //
  // COS-22 puso aquí un `navigate('/login')` gateado por build. Tests verdes, y
  // en el iPhone el logout SEGUÍA cayendo en Sign up. La causa, medida en el
  // código: este `navigate` llega tarde SIEMPRE. `logout()` hace `setUser(null)`
  // (AuthContext:747) y recién DESPUÉS espera lo lento —purga de la bodega
  // (plugins nativos, segundos) y POST de revocación (techo de 10 s)—, así que
  // el render con `user` en null ocurre en ese mismo tick: ProtectedRoute pinta
  // su `<Navigate>` de inmediato y este `await` sigue pendiente durante segundos.
  // El destino real lo fijaba el guard, no esta línea. (Y encima, cuando por fin
  // resolvía, navegaba desde un componente ya desmontado: el drawer entero vive
  // dentro del ProtectedRoute que acaba de expulsarlo.)
  //
  // El fix vive ahora en routeGuards (marca local "ya se inició sesión aquí").
  // En app no se navega desde aquí: sobra —el guard ya aterrizó en /login— y
  // sumaría una segunda entrada al historial hacia el mismo sitio.
  //
  // #477 (t7-1b): EN LA WEB, DIRECTO A LA PORTADA Y EN EL MISMO TICK.
  // Antes la web navegaba a '/' DESPUÉS del `await`, o sea tarde, como el
  // `navigate` de COS-22: el guard ya había pintado /login y Login se veía
  // mientras volaba la revocación. Ahora `navigate('/', { replace: true })` va
  // justo después de llamar a `logout()`, sin nada que esperar en medio: en web
  // `logout` llega a `setUser(null)` sin un solo `await`, y con el router en
  // `useTransitions={false}` (App.js) las dos actualizaciones caen en el MISMO
  // render, cuya ruta ya es la portada (App.js, ruta '/', sin guard). Con
  // `replace` la portada sustituye a la pantalla desde la que se salió, así que
  // en el historial ya no queda ni esa pantalla ni un /login intermedio.
  // Navegar antes de saber si se canceló es seguro sólo porque la guardia de
  // pendientes (D-D) vive detrás de IS_APP_BUILD. Lo fija
  // `logout.sinPestaneo.test.js`.
  const handleLogout = async () => {
    const cierre = logout();
    if (!IS_APP_BUILD) navigate('/', { replace: true });
    // D-D (F6.5b): el logout puede cancelarse desde el aviso de renglones
    // sin sincronizar — en ese caso la sesión se conserva y no se navega.
    const result = await cierre;
    if (result?.cancelled) return;
    toast.success(t('common:toasts.logged_out'));
  };

  /* #447 — UN SOLO PINTOR PARA LOS DOS GRUPOS.
     Los renglones de abajo (Ajustes, Soporte) salen de aquí igual que los de
     arriba, así que no hay dos vestidos que puedan divergir: el candado de
     examen, el `onClose`, el `draggable`, el `nav-no-callout` y el `data-testid`
     derivado de la etiqueta son los MISMOS. El JSX no se reescribió: se movió
     tal cual desde el `.map` de abajo. */
  // dashboard-rediseno: `cuenta` pinta el gris del grupo de abajo; en el menu de iconos el nombre de hoy va como
  // `aria-label` (siempre) y como `title` (solo ahi, donde no se ve escrito).
  const renderNavItem = (item, cuenta = false) => (
    isExamActive ? (
      <button key={item.to} onClick={handleExamBlock}
        aria-label={item.label} title={enIconos ? item.label : undefined}
        className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#333] cursor-not-allowed w-full ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`}
        data-testid={`sidebar-${item.label.toLowerCase().replace(/ /g, '-')}`}>
        <item.icon className="w-4 h-4 shrink-0" />
        <span className={SOLO_CON_NOMBRE}>{item.label}</span>
        {navBadge(item)}
      </button>
    ) : (
      <NavLink key={item.to} to={item.to} onClick={cerrarTodo} draggable={false}
        aria-current={renglonEncendido(item.to, rutaDelMenu, true) ? 'page' : false}
        aria-label={item.label} title={enIconos ? item.label : undefined}
        className={({ isActive }) =>
          `flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
            renglonEncendido(item.to, rutaDelMenu, isActive)
              ? NAV_ENCENDIDO
              : `${cuenta ? NAV_TEXTO_CUENTA : NAV_TEXTO_ESTUDIO} ${NAV_ROW_IDLE}`
          } ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`
        }
        data-testid={`sidebar-${item.label.toLowerCase().replace(/ /g, '-')}`}>
        <item.icon className="w-4 h-4 shrink-0" />
        <span className={SOLO_CON_NOMBRE}>{item.label}</span>
        {navBadge(item)}
      </NavLink>
    )
  );
  const renderNavCuenta = (item) => renderNavItem(item, true);

  // dashboard-rediseno: el nombre de hoy de un renglon como `aria-label` (siempre) y `title` (solo en el menu de
  // iconos, donde no se ve escrito).
  const nombreDelRenglon = (texto) => ({ 'aria-label': texto, title: enIconos ? texto : undefined });
  // Los dos renglones de accion del grupo de la cuenta (cerrar sesion y borrar mis datos) visten igual.
  const RENGLON_DE_ACCION = `flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#A8A8A8] hover:text-red-400 hover:bg-red-500/5 w-full transition-all ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`;

  return (
    <>
    <aside
      className={`
        fixed inset-y-0 left-0 z-[60] ${DRAWER_WIDTH_CLASS} ${abiertoEnIconos ? MENU_ABIERTO_EN_TABLET_CLASS : MENU_ANCHO_CLASS} bg-[#0D0D0D] border-r border-[#1C1C1C] flex flex-col
        transition-transform duration-300
        ${open ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0 ${MENU_DEBAJO_DE_LA_BARRA_CLASS} ${abiertoEnIconos ? 'md:z-[57] shadow-[24px_0_60px_rgba(0,0,0,.55)]' : 'md:z-30'}
      `}
      onClickCapture={(e) => {
        // tutor-1 (D): cerrado en la tablet, cualquier toque sobre el menu de iconos lo ABRE (no navega ni actua)
        if (!enIconos || abiertoEnTablet) return;
        e.preventDefault();
        e.stopPropagation();
        setAbiertoEnTablet(true);
      }}
      data-testid="sidebar"
      data-abierto-en-tablet={abiertoEnIconos ? 'true' : undefined}
    >
      {/* Mobile-only: logo + close button.
          COS-25 — el reloj de iOS pisaba el logo ("Stud6:45ddy"). Este drawer es
          `fixed inset-y-0` (línea 119): un overlay anclado AL VIEWPORT, no un hijo
          del <main> del chasis — o sea que arranca en top:0, por debajo del notch,
          y no hereda la safe area del header (App.js:84). Por eso aquí sí se
          escribe una propia, al contrario del criterio de OfflinePill/Analytics
          (que sí viven dentro del scrollport y heredarían el inset dos veces).
          Sin gate por IS_APP_BUILD a propósito, siguiendo el precedente firmado
          de LanguageSelector.jsx:11-14: `env(...,0px)` COLAPSA A 0px en web, así
          que no hay padding fantasma que gatear, y una PWA en iOS con notch
          también queda cubierta. El p-5 original se conserva en los otros tres
          lados (1.25rem) y solo el tope se desplaza. */}
      <div className={`px-5 pb-5 ${SIDEBAR_HEADER_TOP_CLASS} border-b border-[#1a1a1a] flex items-center justify-between md:hidden`} data-testid="sidebar-mobile-header">
        {/* SPA Link, not <a href>: a raw anchor full-reloads and reboots the SPA in
            the Capacitor WebView (F4.2c bottom-nav disease). onClose: with SPA
            navigation the drawer no longer dies with the document, so close it. */}
        {/* borrar-rotas (A.6 a): la cabecera deja 20 de aire y los iconos de los renglones empiezan a 28; el nombre se
            corre 8 (ALINEACION_DEL_NOMBRE_EN_EL_MENU) para arrancar en la misma linea. La X y la cabecera no se mueven. */}
        <Link to="/" onClick={cerrarTodo} draggable={false} className={`font-['Space_Grotesk'] font-bold text-lg tracking-tight hover:opacity-80 transition-opacity ${ALINEACION_DEL_NOMBRE_EN_EL_MENU} ${NAV_NO_CALLOUT}`} data-testid="sidebar-logo-link">
          Quiz<span className="text-[#FF6B00]">essa</span>
        </Link>
        <button className="text-[#a0a0a0] hover:text-white" onClick={cerrarTodo} data-testid="sidebar-close-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      {abiertoEnIconos ? (
        <div className="hidden md:flex lg:hidden justify-end px-2 pt-2" data-testid="sidebar-tablet-cabecera">
          <button type="button" onClick={() => setAbiertoEnTablet(false)} aria-label={t('common:close')} title={t('common:close')}
            className="w-10 h-10 rounded-xl flex items-center justify-center text-[#9A9A9A] hover:text-white hover:bg-[#181818]"
            data-testid="sidebar-tablet-cerrar">
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      ) : null}
      <nav className={`flex-1 p-3 space-y-0.5 overflow-y-auto${NOSELECT_APP}`}>
        {/* TANDA 11 (firma #177) — EL APRETÓN DE LAS PESTAÑAS.
            Desde #149c los estados de posado del producto viven envueltos en la
            consulta de medio del puntero fino, así que en un teléfono la pestaña
            no tenía NINGÚN estado durante el toque: sólo "normal" y "activa", y
            la activa llega cuando el router aterriza. Peor aquí que en otras
            superficies, porque esta pestaña cierra el drawer en el mismo toque
            (el onClick de abajo) y el naranja de activa no se alcanza a ver.

            El arreglo es un espejo, no un invento: la variante de presión repite
            EXACTAMENTE el mismo par color/fondo que ya sirve al ratón, servido al
            dedo, que nunca dispara el posado. Cero colores nuevos.

            La duración cero no es adorno: estas pestañas arrastran la transición
            de todas las propiedades y un tap dura menos que ella, así que sin
            anularla el resalte no llegaría a verse. Al soltar, la vuelta se anima
            con la transición de siempre.

            Va SÓLO en la rama que no está seleccionada, igual que el posado: la
            pestaña en la que ya estás no promete un viaje. Las variantes
            bloqueadas por el candado de examen no se tocan — un botón muerto no
            responde al dedo a propósito. */}
        {navItems.map(renderNavItem)}
        {isAdmin && (
          <>
            {/* dashboard-rediseno: el grupo del admin, con su linea y su titulo (el titulo se esconde en el menu de
                iconos; la linea se queda). */}
            <div className="mt-3 border-t border-[#1C1C1C] pt-3" data-grupo="admin">
              <p className={`px-4 pb-1 text-[12px] font-semibold tracking-[0.08em] text-[#6A6A6A] ${SOLO_CON_NOMBRE}`}>{t('common:chasis.admin')}</p>
            </div>
            {isExamActive ? (
              <button onClick={handleExamBlock}
                aria-label={t('nav.admin_panel')} title={enIconos ? t('nav.admin_panel') : undefined}
                className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#333] cursor-not-allowed w-full ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`}
                data-testid="sidebar-admin-dashboard">
                <Activity className="w-4 h-4 shrink-0" />
                <span className={SOLO_CON_NOMBRE}>{t('nav.admin_panel')}</span>
              </button>
            ) : (
              <NavLink to="/admin/dashboard" onClick={cerrarTodo} draggable={false}
                className={({ isActive }) =>
                  `flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? NAV_ENCENDIDO
                      : 'text-[#C8C8C8] hover:text-[#FF6B00] hover:bg-[#FF6B00]/5 active:text-[#FF6B00] active:bg-[#FF6B00]/5 active:duration-0'
                  } ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`
                }
                aria-label={t('nav.admin_panel')} title={enIconos ? t('nav.admin_panel') : undefined}
                data-testid="sidebar-admin-dashboard">
                <Activity className="w-4 h-4 shrink-0" />
                <span className={SOLO_CON_NOMBRE}>{t('nav.admin_panel')}</span>
              </NavLink>
            )}
            {isExamActive ? (
              <button onClick={handleExamBlock}
                aria-label={t('nav.error_log')} title={enIconos ? t('nav.error_log') : undefined}
                className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#333] cursor-not-allowed w-full ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`}
                data-testid="sidebar-error-log">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className={SOLO_CON_NOMBRE}>{t('nav.error_log')}</span>
              </button>
            ) : (
              <NavLink to="/admin/errors" onClick={cerrarTodo} draggable={false}
                className={({ isActive }) =>
                  `flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? NAV_ENCENDIDO
                      : 'text-[#C8C8C8] hover:text-red-400 hover:bg-red-500/5 active:text-red-400 active:bg-red-500/5 active:duration-0'
                  } ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`
                }
                aria-label={t('nav.error_log')} title={enIconos ? t('nav.error_log') : undefined}
                data-testid="sidebar-error-log">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className={SOLO_CON_NOMBRE}>{t('nav.error_log')}</span>
              </NavLink>
            )}
            {/* ── TANDA 12 (firma #182) — Y LA PUERTA AL VISOR VUELVE, AQUÍ ────
                La lápida de abajo cuenta por qué se fue del drawer en AS-6f y la
                de `pages/SettingsPage.js` cuenta la vuelta y la ida de la TANDA
                11. Ésta es la mudanza que la deja donde va: junto a sus dos
                hermanas de administrador, que es donde el dueño busca sus
                herramientas internas.

                DOS LLAVES, y las dos hacen falta: la del bloque de arriba
                (`isAdmin`, que desde esta tanda descarta la cadena vacía) y la
                del build — en web la ruta del visor NI SIQUIERA SE REGISTRA, así
                que una entrada ahí llevaría al vacío. Las dos entradas de
                administrador de arriba NO llevan la del build a propósito: sus
                rutas sí existen en los dos builds desde esta misma tanda.

                EL TEXTO NO ES NUEVO: son las MISMAS llaves `diagnostics.*` de
                `settings.json` que estrenó la TANDA 11, leídas con su namespace
                por delante (por eso este componente declara los dos). Cero
                cadenas nuevas, cero bust que subir.

                Y la pantalla de detrás sigue preguntando lo mismo al entrar
                (`DiagReadyChainPage.js`): la puerta nunca fue el único candado. */}
            {IS_APP_BUILD && (
              isExamActive ? (
                <button onClick={handleExamBlock}
                  aria-label={t('settings:diagnostics.heading')} title={enIconos ? t('settings:diagnostics.heading') : undefined}
                  className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#333] cursor-not-allowed w-full ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`}
                  data-testid="sidebar-diag">
                  <Stethoscope className="w-4 h-4 shrink-0" />
                  <span className={SOLO_CON_NOMBRE}>{t('settings:diagnostics.heading')}</span>
                </button>
              ) : (
                <NavLink to="/diag/ready-chain" onClick={cerrarTodo} draggable={false}
                  className={({ isActive }) => `flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? NAV_ENCENDIDO
                      : 'text-[#C8C8C8] hover:text-[#FF6B00] hover:bg-[#FF6B00]/5 active:text-[#FF6B00] active:bg-[#FF6B00]/5 active:duration-0'
                  } ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`}
                  aria-label={t('settings:diagnostics.heading')} title={enIconos ? t('settings:diagnostics.heading') : undefined}
                  data-testid="sidebar-diag">
                  <Stethoscope className="w-4 h-4 shrink-0" />
                  <span className={SOLO_CON_NOMBRE}>{t('settings:diagnostics.heading')}</span>
                </NavLink>
              )
            )}
          </>
        )}
        {/* ── AS-6f — AQUÍ VIVÍA LA PUERTA AL VISOR (FIRMA #38, opción a) ─────
            De cara al envío 1.0.0 el renglón «Diagnóstico» sale del drawer: un
            revisor de tienda no debe toparse con una herramienta interna, sin
            i18n y en español, colgando del menú principal.

            LO QUE SE FUE: sólo el renglón (el de testid `sidebar` + `-diag`),
            que COS-G B11 había puesto aquí como excepción declarada al candado
            de examen — esa excepción ya no tiene a quién eximir y se va con él.

            LO QUE NO SE FUE (la vigilancia automática sigue viva): la ruta
            `/diag/ready-chain` sigue registrada en `App.js` (sin guard), el
            visor `DiagReadyChainPage` sigue montado ahí, y toda la
            instrumentación que lo alimenta —el tripwire `NAV.H1`
            (`lib/diag/carrilTripwire.js`), las líneas `NAV.*`/`FLASH.*`/`CV.*`/
            `DL.*`/`SCR.*` y la Bomba `console.warn`— quedó intacta.

            Guarda de ausencia: `Sidebar.visorCosG.test.js` (re-anclada de exigir
            presencia a exigir AUSENCIA) y el barrido de repo en
            `lib/as6f.visorFuera.test.js`.

            ── TANDA 12 (firma #182): EL RENGLÓN VOLVIÓ, unas líneas más arriba y
            con DOS llaves (administrador + build de app), dentro del bloque de
            las entradas de administrador. Esta lápida se queda porque cuenta el
            porqué de la salida, que sigue siendo verdad para todos menos para el
            dueño: un revisor de tienda no ve esta entrada, y una cuenta cualquiera
            tampoco. Lo que cambió no es la regla, es DÓNDE se cumple. Y la
            excepción al candado de examen que COS-G B11 le había dado NO volvió:
            el renglón nuevo se apaga con sus hermanas. */}
      </nav>
      {/* Session block. pb: keeps the guest Log in / Create Account rows clear of the
          iPhone home indicator (env() is 0 on desktop, so the web rail is unchanged). */}
      <div className={`p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-[#1a1a1a]${NOSELECT_APP}`}>
        {/* LAPIDA post-t7-2c (D1, #640): aqui vivia la tarjeta de la app de la TANDA 27b (variante
            panel-lateral, encima de "Idioma"). Carlos la saco del Panel: la barra ya no la monta, ni
            para usuario ni para invitado, en la web ni en la app. */}
        {/* #463 (app-3) -- EL ROTULO DE INVITADO, ARRIBA DEL GRUPO DE ABAJO. Vivia
            bajo "Idioma", encima de "Iniciar sesion"; ahora va inmediatamente arriba
            de "Soporte" (para un invitado "Ajustes" no se pinta, #435). Es el MISMO
            nodo con el mismo vestido y la misma llave: solo cambio de sitio. La app
            lo hereda sola: su boton "Mas" abre este mismo cajon. */}
        {isGuest && (
          <p className={`px-4 pb-1 text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6A6A6A] ${SOLO_CON_NOMBRE}`}>{t('user_card.guest_session')}</p>
        )}
        {/* #447 — LOS DOS RENGLONES DE LA CUENTA, ARRIBA DEL IDIOMA.
            Ajustes y Soporte salen del MISMO pintor que los de arriba (ver
            `renderNavItem`), así que su vestido, su candado de examen y su
            `data-testid` son idénticos a los de antes de la mudanza. */}
        <div className="space-y-0.5 mb-1">
          {bottomItems.map(renderNavCuenta)}
        </div>
        {/* Language switch — for EVERYONE, no branching by user type: guests have no
            Settings access (and the app build has no landing page), logged-in users
            gain convenience. Goes through the single central mechanism
            (lib/language.js). Deliberately NOT behind the mid-session lock: it
            doesn't navigate, and switching language must not break a session.
            Native names come from SUPPORTED_LANGUAGES and are never translated.

            #447 — AHORA ES PLEGABLE, y nada más cambia. El renglón de arriba es un
            botón con `aria-expanded` y su galoncito; cerrado por omisión. Al abrirlo
            salen debajo las dos filas, English y Español, cada una con su `lang`. La
            activa lleva EXACTAMENTE el mismo par de clases que la marcaba antes, y
            al elegir una se llama a `changeAppLanguage(code, user)` — la misma
            función, con los mismos argumentos, y por tanto la misma persistencia —
            y la fila se vuelve a plegar. */}
        <div className="mb-1" data-testid="sidebar-language">
          {/* dashboard-rediseno: "Language" ensena el codigo del idioma de hoy ("EN" o "ES") y al tocarlo hace lo de
              siempre (abre las dos filas). */}
          <button
            type="button"
            onClick={() => setIdiomaAbierto(abierto => !abierto)}
            aria-expanded={idiomaAbierto}
            aria-label={t('nav.language')}
            title={enIconos ? t('nav.language') : undefined}
            className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium w-full transition-all ${NAV_TEXTO_CUENTA} ${NAV_ROW_IDLE} ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`}
            data-testid="sidebar-language-toggle"
          >
            <Languages className="w-4 h-4 shrink-0" />
            <span className={SOLO_CON_NOMBRE}>{t('nav.language')}</span>
            <span className={`ml-auto text-[12px] font-semibold text-[#6A6A6A] ${SOLO_CON_NOMBRE}`} data-idioma-codigo="">
              {currentLang.toUpperCase()}
            </span>
          </button>
          {idiomaAbierto && (
            <div className="space-y-0.5 mt-0.5" data-testid="sidebar-language-options">
              {SUPPORTED_LANGUAGES.map(({ code, nativeName }) => (
                <button
                  key={code}
                  type="button"
                  lang={code}
                  aria-label={nativeName}
                  onClick={() => { changeAppLanguage(code, user); setIdiomaAbierto(false); }}
                  className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium w-full transition-colors ${NAV_RENGLON_ICONOS} ${
                    currentLang === code
                      ? 'bg-[#FF6B00] text-white'
                      : 'bg-[#1a1a1a] text-[#999] hover:bg-[#2a2a2a]'
                  } ${NAV_NO_CALLOUT}`}
                  data-testid={`sidebar-lang-${code}`}
                >
                  {enIconos ? code.toUpperCase() : nativeName}
                </button>
              ))}
            </div>
          )}
        </div>
        {isGuest ? (
          <div className="space-y-1">
            {isExamActive ? (
              <>
                <button onClick={handleExamBlock} {...nombreDelRenglon(t('nav.log_in'))} className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#333] cursor-not-allowed w-full ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`} data-testid="sidebar-login">
                  <LogIn className="w-4 h-4 shrink-0" /> <span className={SOLO_CON_NOMBRE}>{t('nav.log_in')}</span>
                </button>
                <button onClick={handleExamBlock} {...nombreDelRenglon(t('nav.create_account'))} className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#333] cursor-not-allowed w-full ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`} data-testid="sidebar-signup">
                  <UserPlus className="w-4 h-4 shrink-0" /> <span className={SOLO_CON_NOMBRE}>{t('nav.create_account')}</span>
                </button>
                {/* #466 -- a media sesion se apaga igual que sus dos vecinas. */}
                <button onClick={handleExamBlock} {...nombreDelRenglon(t('dashboard:guest_banner.delete_data'))} className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#333] cursor-not-allowed w-full ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`} data-testid="sidebar-delete-data">
                  <Trash2 className="w-4 h-4 shrink-0" /> <span className={SOLO_CON_NOMBRE}>{t('dashboard:guest_banner.delete_data')}</span>
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login" onClick={cerrarTodo} draggable={false} {...nombreDelRenglon(t('nav.log_in'))}
                  className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#A8A8A8] hover:text-white hover:bg-[#181818] transition-all ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`}
                  data-testid="sidebar-login">
                  <LogIn className="w-4 h-4 shrink-0" /> <span className={SOLO_CON_NOMBRE}>{t('nav.log_in')}</span>
                </NavLink>
                <NavLink to="/signup" onClick={cerrarTodo} draggable={false} {...nombreDelRenglon(t('nav.create_account'))}
                  className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-semibold text-[#F7A06A] hover:bg-[#FF6B00]/10 transition-all ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`}
                  data-testid="sidebar-signup">
                  <UserPlus className="w-4 h-4 shrink-0" /> <span className={SOLO_CON_NOMBRE}>{t('nav.create_account')}</span>
                </NavLink>
                {/* #466 -- el ultimo renglon del invitado. Es una ACCION, no una liga:
                    viste como "Cerrar sesion", el otro renglon de accion de este bloque
                    (misma cadena de clases), con el icono de borrar cuenta de Ajustes.
                    Cierra el cajon como sus vecinas y abre la ventana de siempre. */}
                <button type="button" onClick={() => { onClose(); setBorrarDatosAbierto(true); }} {...nombreDelRenglon(t('dashboard:guest_banner.delete_data'))}
                  className={RENGLON_DE_ACCION}
                  data-testid="sidebar-delete-data">
                  <Trash2 className="w-4 h-4 shrink-0" /> <span className={SOLO_CON_NOMBRE}>{t('dashboard:guest_banner.delete_data')}</span>
                </button>
              </>
            )}
          </div>
        ) : (
          isExamActive ? (
            <button onClick={handleExamBlock} {...nombreDelRenglon(t('nav.logout'))}
              className={`flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-medium text-[#333] cursor-not-allowed w-full ${NAV_RENGLON_ICONOS} ${NAV_NO_CALLOUT}`}
              data-testid="sidebar-logout">
              <LogOut className="w-4 h-4 shrink-0" /> <span className={SOLO_CON_NOMBRE}>{t('nav.logout')}</span>
            </button>
          ) : (
            <button onClick={handleLogout} {...nombreDelRenglon(t('nav.logout'))}
              className={RENGLON_DE_ACCION}
              data-testid="sidebar-logout">
              <LogOut className="w-4 h-4 shrink-0" /> <span className={SOLO_CON_NOMBRE}>{t('nav.logout')}</span>
            </button>
          )
        )}
      </div>
    </aside>
    {abiertoEnIconos ? (
      <div className={`hidden md:block lg:hidden fixed inset-x-0 bottom-0 ${VELO_DEL_MENU_DE_TABLET_CLASS} bg-black/45 z-[56] cursor-pointer`}
        onClick={() => setAbiertoEnTablet(false)} data-testid="sidebar-velo-tablet" />
    ) : null}
    {/* #466 -- LA VENTANA VA COMO HERMANA DEL ASIDE, NUNCA DENTRO. El aside lleva la
        transformacion del deslizado del cajon, y un elemento fijo dentro de un
        ancestro transformado deja de medirse contra la pantalla: al cerrarse el
        cajon, la ventana se iria con el. Aqui cuelga del mismo contenedor del
        chasis que `PendingGroupJoinModal`. Solo existe para el invitado. */}
    {isGuest && borrarDatosAbierto && (
      <Suspense fallback={null}>
        <DeleteAccountModal open onClose={() => setBorrarDatosAbierto(false)} />
      </Suspense>
    )}
    </>
  );
}
